import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl, IntlProvider as IP } from 'react-intl';
import { Route, Switch } from 'react-router-dom';
import { i18nIntl, getRemotedbUrl, preventSelection, getLayout, getLayoutStage, isNotEmpty } from '../../lib/ep.js';
import { slice as _slice, shuffle as _shuffle } from 'lodash';

import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';

import ActionButton, { SecondaryButton, SquaredButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderSidebar from '../builder/BuilderSidebar';
import ButtonCollection from '../builder/ButtonCollection.js';
import { DeleteButton } from '../builder/ConfirmButton';
import FloatingLayer from '../builder/FloatingLayer';
import InputWithButton from '../builder/InputWithButton';
import { AlertMessage } from '../builder/Message';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Range from '../builder/Range';
import RichEditor from '../builder/RichEditor';
import SaveButton from '../builder/SaveButton';
import SectionTitle from '../builder/SectionTitle';
import ToggleButton from '../builder/ToggleButton';
import Tooltip from '../builder/Tooltip.js';
import BuilderMain, { BuilderMainWithoutSidebar } from '../builder/BuilderMain.js';

import TriviaSidebar from './TriviaSidebar';

import Layout from '../front/Layout';
import TriviaClient from '../front/TriviaClient';
import TriviaQuestion from '../front/TriviaQuestion';
import RibbonCollection from '../ui_items/RibbonCollection.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import ColorPicker from '../builder/ColorPicker.js';
import MultiOptionsCreator from './MultiOptionsCreator.js';
import Checkbox from '../builder/Checkbox.js';
import Select from '../builder/Select.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import DisabledArea from '../builder/DisabledArea.js';

const DragHandle = SortableHandle(() => <span class="sorter_target" />); // This can be any component you want

const SortableQuestions = SortableContainer(({ items, field, intl, editQuestion, removeQuestion }) => {
	return (
		<tbody>
			{items.map((value, index) => {
				const obj_index = index;

				return (
					<SortableQuestion
						key={`sqitem-${index}`}
						index={index}
						obj_index={obj_index}
						value={value}
						editQuestion={editQuestion}
						removeQuestion={removeQuestion}
						intl={intl}
					/>
				)
			})}
		</tbody>
	);
});

const SortableQuestion = SortableElement(({ value, obj_index, intl, editQuestion, removeQuestion }) => {
	return (
		<Question
			intl={intl}
			index={obj_index}
			{...value}
			editQuestion={editQuestion}
			removeQuestion={removeQuestion}
			sortable={true}
		/>
	)}
);

const SortableAnswers = SortableContainer(({ items, type, intl, editAnswer, removeAnswer, changeCorrectAnswer }) => {
	return (
		<div>
			{items.map((value, index) => {
				const obj_index = index;
				return (
					<SortableAnswer
						key={`saitem-${index}`}
						index={index}
						type={type}
						obj_index={obj_index}
						value={value}
						editAnswer={editAnswer}
						removeAnswer={removeAnswer}
						changeCorrectAnswer={changeCorrectAnswer}
						intl={intl}
					/>
				)
			})}
		</div>
	);
});

const SortableAnswer = SortableElement(({ value, obj_index, intl, type, editAnswer, removeAnswer, changeCorrectAnswer }) => {
	return (
		<Answer
			intl={intl}
			index={obj_index}
			answer={value}
			type={type}
			editAnswer={editAnswer}
			removeAnswer={removeAnswer}
			changeCorrectAnswer={changeCorrectAnswer}
			sortable={true}
		/>
	)}
);

const Answer = (props) => {
	const answer = props.answer;

	return (
		<li class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_nowrap gui_flex_align_center onZTop">
			<div class="item_header" style={{width:'calc(100% - 105px)'}}>
				<DragHandle />

				<div class="item_title_container" style={{maxWidth:'100%'}}>
					<ActionLink _class={{other:'item_title without_icon cutTextOnOverflow gui_withoutEvents'}} autotranslate={false}>
						<strong>{answer.answer}</strong>
					</ActionLink>
				</div>
			</div>

			<ButtonCollection _class={{other:'gui_is_padded_small'}}>
				{(props.type == 1) && (
					<ToggleButton
						squared={true}
						active={(answer.correct == "1")}
						onClick={() => props.changeCorrectAnswer(answer)}
						icon='knk-on greenButton'
						icon_off='knk-off'
						color={(answer.correct == "1") ? 'white' : 'black'}
						_class={{first:true, other:((answer.correct == "1") ? 'gui_withoutEvents' : '')}}
					/>
				)}

				{(answer.id != undefined) && (
					<SquaredButton
						icon='knk-edit'
						onClick={() => props.editAnswer(answer)}
					/>
				)}

				<DeleteButton
					squared={true}
					_position="left"
					onClick={() => props.removeAnswer(answer)}
				/>
			</ButtonCollection>
		</li>
	)
};

const Question = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const question = props.Question;
	const answers = props?.Answer ?? [];

	let answer_ribbons = [];
	answers.map((answer) => {
		answer_ribbons.push({
			title:answer.answer.toUpperCase(),
			color:(answer.correct == '1') ? 'green' : 'gray',
			icon:(answer.correct == '1') ? 'knk-on' : 'knk-off',
		});
	});

	return (
		<tr class="gui_sortable_list_item">
			<td class="item_header" style={{height:'auto', minHeight:'51px'}}>
				<DragHandle />

				<div class="item_title_container" style={{maxWidth:'100%'}}>
					<ActionLink
						_class={{other:'without_icon gui_padded_in_start item_title cutTextOnOverflow gui_withoutEvents'}}
						autotranslate={false}
					><strong>{question.question}</strong></ActionLink>

					<div class="gui_not_in_top_tiny">
						<span class="gui_flexbox_auto gui_flex_align_center">
							<span class="gui_not_in_end_small">{i18n("Answers")}:</span>

							<RibbonCollection
								items={answer_ribbons}
							/>
						</span>
					</div>
				</div>
			</td>

			<td>
				<ButtonCollection>
					<SquaredButton
						icon='knk-edit'
						onClick={() => props.editQuestion(question.id)}
						aria-label="Edit question"
					/>

					<DeleteButton
						squared={true}
						_position="left"
						onClick={() => props.removeQuestion(question)}
						aria-label="Delete question"
					/>
				</ButtonCollection>
			</td>
		</tr>
	)
};

const TriviaBuilder = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = props.stage;
	const questions = props?.questions ?? {};

	const default_answer = {
		answer:"",
		config:{
			type:'closed_text',
		}
	};

	const default_question = {
		Question:{
			question:i18n("Type your question here..."),
			answers_row:0,
			config:{},
			multianswer:0,
			is_advanced:0,
			question_type:'1',
			type:'1',
			image:'',
		},
		Answer:[],
	};
	if(props.stage_id != undefined) default_question.Question.stage = props.stage_id;

	const [ display_question_layer, setDisplayQuestionLayer ] = useState(false);
	const [ edit_question_id, setEditQuestionID ] = useState(null);
	const [ question_tmp, setQuestionTmp ] = useState(default_question);
	const [ answer_tmp, setAnswerTmp ] = useState(default_answer);
	const [ user, setUser ] = useState({});
	const [ random_instance, setRandomInstance ] = useState(Math.random() * 10000);
	const [ promotion, setPromotion ] = useState(questions.pm.attributes);
	const [ final_case, setFinalCase ] = useState('success');

	const max_answers = 5;

	useEffect(() => {
		changeCssVariable();
		resetQuestion();
		resetAnswer();
	}, []);

	const layout_props_pm = getLayout(promotion);
	layout_props_pm.stage = getLayoutStage(props.stage, layout_props_pm);

	const changeCssVariable = () => {
		if(window.css_var.setCustomStyles != undefined) {
			window.css_var.setCustomStyles(layout_props_pm);
		}
	};

	const changeLayout = (scope, k, v) => {
		if(props.stage.layout_props == undefined) props.stage.layout_props = {};
		if(props.stage.layout_props[scope] == undefined) props.stage.layout_props[scope] = {};

		if(v != undefined && v != '') {
			props.stage.layout_props[scope] = { ...props.stage.layout_props[scope], [k]:v };
		} else if(props.stage.layout_props[scope][k] != undefined) {
			delete props.stage.layout_props[scope][k];

			if(Object.keys(props.stage.layout_props[scope]).length == 0) {
				delete props.stage.layout_props[scope];
			}

			props.stage.layout_props = { ...props.stage.layout_props };
		}

		layout_props_pm.stage = getLayoutStage(props.stage, layout_props_pm);
		props.changeStage('layout_props', props.stage.layout_props);

		changeCssVariable();
	};

	const onChangeFinalCase = (id) => {
		setFinalCase(id);
	};

	const resetQuestion = () => {
		setQuestionTmp(default_question);
		setEditQuestionID(null);
	};

	const resetAnswer = () => {
		setAnswerTmp(default_answer);
	};

	const changeQuestion = (id, k, v) => {
		if(id != undefined) {
			props.dispatch({
				k:k,
				qid:id,
				type:'CHANGEQUESTION',
				v:v,
			});
			return;
		}

		const _question_tmp = { ...question_tmp };
		_question_tmp.Question[k] = v;
		setQuestionTmp(_question_tmp);
	};

	const resetAllQuestions = () => {
		if((questions?.questions ?? []).length == 0) return;

		questions.questions.map((q) => {
			removeQuestion(q.Question);
		});
	};

	const removeQuestion = (question) => {
		props.dispatch({
			qid:question.id,
			type:'DELETEQUESTION',
		});
		$.post(getRemotedbUrl('/quizs/deleteQuestion'), { promotion_id:question.promotion_id, id:question.id });
		props.questionStore.dispatch({type:'DELETEQUESTION',qid:question.id});
	};

	const onSortEndQuestions = ({ oldIndex, newIndex }) => {
		props.dispatch({
			newIndex:newIndex,
			oldIndex:oldIndex,
			type:'SORTQUESTION',
		});

		saveQuiz();
	};

	const saveQuiz = (e, cb) => {
		$.post(getRemotedbUrl(`/quizs/save/${promotion.id}`), {
			questions:JSON.stringify(questions.questions),
		}, (r) => {
			resetAnswer();
			if(cb != undefined) cb();
		});
	};

	const addQuestionAndSaveQuiz = (e, cb) => {
		$.post(getRemotedbUrl(`/quizs/addquestion/${promotion.id}/`), question_tmp.Question, (response) => {
			if(Array.isArray(response.Question.config)) response.Question.config = {};

			props.dispatch({ type:'ADDQUESTION', questions:response });

			question_tmp.Answer.map((ans, index) => {
				ans.question_id = response.Question.id;
				ans.pos = (index + 1);
				$.post(getRemotedbUrl(`/quizs/addanswer/${promotion.id}`), ans, (res_2) => {
					if(!(response instanceof Array)) res_2 = [res_2];
					props.dispatch({
						answers:res_2,
						qid:response.Question.id,
						type:'ADDANSWERS',
					});

					if((index + 1) == question_tmp.Answer.length) {
						$.post(getRemotedbUrl(`/quizs/save/${promotion.id}`), { questions:JSON.stringify(questions.questions) }, (r) => {
							if(cb != undefined) cb();
							closeQuestionEditLayer();
							props.questionStore.dispatch({ type:'SETQUIZ', questions:r });
						}, 'JSON');
					}
				}, 'JSON');
			});
		}, 'JSON');
	};

	const addAnswer = (question_id) => {
		const _answer_tmp = { ...answer_tmp };

		if(_answer_tmp.answer.trim() == "") return;

		if(question_id == undefined) {
			_answer_tmp.answer = (isNotEmpty(_answer_tmp, 'answer')) ? _answer_tmp.answer : i18n("New answer");

			const _question_tmp = { ...question_tmp };
			_question_tmp.Answer.push(_answer_tmp);
			setQuestionTmp(_question_tmp);
			resetAnswer();
			return;
		}

		if(_answer_tmp.id != undefined) {
			//editing
			changeAnswer(_answer_tmp.question_id, _answer_tmp.id, 'answer', _answer_tmp.answer);
			resetAnswer();
			return;
		}

		const d = {
			answer:(isNotEmpty(_answer_tmp, 'answer')) ? _answer_tmp.answer : i18n("New answer"),
			config:(isNotEmpty(_answer_tmp, 'config')) ? _answer_tmp.config : default_answer.config,
			question_id,
		};

		$.post(getRemotedbUrl(`/quizs/addanswer/${promotion.id}`), d, (res) => {
			if(!(res instanceof Array)) res = [res];

			props.dispatch({
				answers:res,
				qid:question_id,
				type:'ADDANSWERS',
			});

			resetAnswer();
		}, 'JSON');
	};

	const removeAnswer = (answer) => {
		if(answer.id != undefined && answer.question_id != undefined) {
			props.dispatch({
				aid:answer.id,
				qid:answer.question_id,
				type:'DELETEANSWER',
			});

			$.post(getRemotedbUrl('/quizs/deleteanswer'), { id:answer.id });
			return;
		}

		const _question_tmp = { ...question_tmp };
		const ans = _question_tmp.Answer.find((obj) => (answer.answer == obj.answer));
		const key_answer = _question_tmp.Answer.indexOf(ans);
		_question_tmp.Answer.splice(key_answer, 1);
		setQuestionTmp(_question_tmp);
	};

	const changeAnswer = (qid, answer_id, k, v) => {
		props.dispatch({
			aid:answer_id,
			k,
			qid,
			type:'CHANGEANSWER',
			v,
		});
	};

	const onSortEndAnswers = ({ oldIndex, newIndex }, question_id) => {
		if(question_id != undefined) {
			props.dispatch({
				newIndex:newIndex,
				oldIndex:oldIndex,
				qid:question_id,
				type:'SORTANSWER',
			});
			return;
		}

		const _question_tmp = { ...question_tmp };
		_question_tmp.Answer = arrayMove(_question_tmp.Answer, oldIndex, newIndex);
		setQuestionTmp(_question_tmp);
	};

	const onClientAnswer = (_user) => {
		setUser(_user);
	};

	const closeQuestionEditLayer = () => {
		resetQuestion();
		resetAnswer();
		setEditQuestionID(null);
		setDisplayQuestionLayer(false);
	};

	const openQuestionEditLayer = (id) => {
		resetQuestion();
		resetAnswer();
		setEditQuestionID(id);
		setDisplayQuestionLayer(true);
	};

	const transferQuestions = (new_questions, cb) => {
		props.dispatch({ type:'ADDQUESTION', questions:new_questions });
		props.questionStore.dispatch({ type:'ADDQUESTION', questions:new_questions });
		if(cb != undefined) cb();
	};

	const changeCorrectAnswer = (answer) => {
		if(answer.id != undefined) {
			const question = (questions?.questions ?? []).find((obj) => (obj.Question.id == answer.question_id));

			question.Answer.map((ans) => {
				const new_status = (answer.id == ans.id) ? '1' : '0';
				changeAnswer(ans.question_id, ans.id, 'correct', new_status);
			});
			return;
		}

		const _question_tmp = { ...question_tmp };
		_question_tmp.Answer.map((ans, j) => {
			const new_status = (answer.answer == ans.answer) ? '1' : '0';
			_question_tmp.Answer[j].correct = new_status;
		});

		setQuestionTmp(_question_tmp);
	};

	const changeAnswerTmp = (k, v) => {
		setAnswerTmp({ ...answer_tmp, [k]:v });
	};

	const setTriviaForBuilder = (_questions, with_conditions = true) => {
		if(with_conditions === false) return _questions;

		const stage = props.stage;
		const max_questions = stage?.max_questions ?? _questions.length;

		if(stage?.random_answers ?? false) {
			_questions.map((question, index) => {
				_questions[index].Answer = _shuffle(question.Answer);
			});
		}

		if(stage?.random_questions ?? false) _questions = _shuffle(_questions);
		_questions = _slice(_questions, 0, max_questions);
		return _questions;
	};

	const canPlayAgain = () => {
		setUser({});
		setRandomInstance(Math.random() * 100000);
		if(props.canPlayAgain != undefined) props.canPlayAgain();
	};

	const is_draft = (promotion.status == '0') ? true : false;
	const all_questions = (questions?.questions ?? []).filter((obj) => (obj.Answer.length >= 0));

	const base = props.basename;
	const section_main_path = `${base}/trivia`;

	const in_stages = (base.match(/stages\/?/i) != null) ? true : false;

	const sections = [
		{ label:"Questions", link:`${section_main_path}/questions`, icon:'knk-questions' },
		{ label:"Gameplay settings", link:`${section_main_path}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${section_main_path}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${section_main_path}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${section_main_path}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${section_main_path}/testreact`, icon:'knk-ok' },
	];
	if(in_stages) sections.unshift({ label:"Back to Stages", link:`${base}/options`, icon:'knk-go-prev' });
	
	return (
		<>
			<Switch>
				<Route exact={true} path={`/${section_main_path}/questions`} render={(route) => {
					const actual_question = ((edit_question_id != null)) ? all_questions.find((q) => (q.Question.id == edit_question_id)) : { ...question_tmp };

					const at_least_one_correct_answer = ((actual_question?.Answer ?? []).filter((obj) => (obj.correct == '1')).length > 0) ? true : false;

					const filtered_questions = setTriviaForBuilder(all_questions, false);

					let layer_title;
					let button_label;
					let button_color;
					let button_action;
					if(actual_question?.Question?.id == undefined) {
						layer_title = "Create a new question";
						button_label = "Add";
						button_color = 'green';
						button_action = addQuestionAndSaveQuiz;
					} else {
						layer_title = "Edit question";
						button_label = "Save";
						button_color = 'blue';
						button_action = saveQuiz;
					}

					let error_add = false;
					if((actual_question.Question.question).trim() == "") {
						error_add = "You can't create an empty question";
					} else if(actual_question.Answer.length < 2) {
						error_add = "You need to add at least 2 answer options";
					} else if(!at_least_one_correct_answer) {
						error_add = "You need to mark a correct answer";
					}

					const question_image = actual_question?.Question?.full_image ?? (actual_question?.Question?.fullimage ?? '');
					return (
						<BuilderMainWithoutSidebar pm={questions.pm} has_tabs={true}>
							<NavigationTabsBuilder
								active={route.location.pathname}
								sections={sections}
							>{props.stage_indicator}</NavigationTabsBuilder>

							<section class="not_awesome">
								<div class="gui_section_header alone_section_header cleared_header">
									<div class="gui_flexbox_auto gui_flex_align_baseline">
										<SectionTitle
											autotranslate={false}
											_class={{ first:true }}
										>{`${i18n("Questions")} (${filtered_questions.length})`}</SectionTitle>
									</div>

									<ButtonCollection>
										<MultiOptionsCreator
											type_ai='timed_quiz'
											promotion={promotion}
											stage_id={props.stage_id}
											transferQuestions={transferQuestions}
											onCreateSimple={() => setDisplayQuestionLayer(true)}
										/>

										{(is_draft && filtered_questions.length > 0) && (
											<DeleteButton
												onClick={resetAllQuestions}
											>Delete all questions</DeleteButton>
										)}
									</ButtonCollection>
								</div>

								<div class="gui_table_with_graphic">
									{(filtered_questions.length > 0) && (
										<div class="not_in_top in_bottom" style={{ overflow:'auto', maxHeight:'calc(100dvh - 260px)' }}>
											<table class="gui_list_table" style={{ width:'100%' }} cellSpacing="0" cellPadding="0">
												<thead>
													<tr class="list_header">
														<th>{i18n("Question")}</th>

														<th class="gui_text_align_end">{i18n("Actions")}</th>
													</tr>
												</thead>

												<SortableQuestions
													items={filtered_questions}
													intl={props.intl}
													editQuestion={(id) => openQuestionEditLayer(id)}
													removeQuestion={(q) => removeQuestion(q)}
													onSortStart={() => preventSelection(true)}
													onSortEnd={onSortEndQuestions}
													useDragHandle={true}
												/>
											</table>
										</div>
									)}
								</div>
							</section>

							{(display_question_layer) && (
								<FloatingLayer backButton={closeQuestionEditLayer} enable_close={false}>
									<div>
										<SectionTitle>{layer_title}</SectionTitle>

										<div class="edit_with_preview">
											<div>
												<DisabledArea
													disabled={(question_image != '') ? true : false}
												>
													<Model
														model={actual_question.Question}
														change={(k, v) => changeQuestion(actual_question.Question.id, k, v)}
													>
														<RichEditor
															name="question"
															display_wysiwyg_buttons={false}
															has_emojis={true}
															_class={{ first:true, v_size:'normal' }}
															max_length={150}
														>Question text</RichEditor>
													</Model>
												</DisabledArea>

												<Model
													model={actual_question.Question}
													change={(k, v) => changeQuestion(actual_question.Question.id, k, v)}
												>
													<AutoPostImageUploader
														name="image"
														auto_url={`/promotions/auto_img/${promotion.id}`}
														auto_type='quiz'
														small_input={true}
														image={question_image}
														_size={{ width:800, height:500 }}
													>Question image</AutoPostImageUploader>
												</Model>

												<div class="gui_not_in_top_big">
													<BuilderSubSectionTitle
														title={`${i18n("Answers")} (${actual_question.Answer.length})`}
													/>

													<div>
														{(actual_question.Answer.length < max_answers) && (
															<div class="gui_not_in_top_tiny gui_not_in_bottom_tiny">
																<div class="gui_flexbox">
																	<Model
																		model={answer_tmp}
																		change={(k, v) => changeAnswerTmp(k, v, 'answer_tmp')}
																	>
																		<InputWithButton
																			execute_on_empty={true}
																			name="answer"
																			has_emojis={true}
																			_class={{ first:true }}
																			submitColor={(answer_tmp.id != undefined) ? 'gray' : 'green'}
																			submitIcon={(answer_tmp.id != undefined) ? 'knk-save' : 'knk-add-item'}
																			error_msg_position_class="is_floating"
																			placeholder={i18n("Answer text")}
																			max_length={30}
																			value={answer_tmp?.answer ?? ""}
																			onSubmit={() => addAnswer(actual_question.Question.id)}
																		/>
																	</Model>
																</div>
															</div>
														)}

														{(actual_question.Answer.length > 0) && (
															<ul class="gui_sortable_list gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_overflowed_auto">
																<SortableAnswers
																	items={actual_question.Answer}
																	type={actual_question.Question.type}
																	removeAnswer={(a) => removeAnswer(a)}
																	changeCorrectAnswer={(a) => changeCorrectAnswer(a)}
																	editAnswer={(a) => setAnswerTmp(a)}
																	onSortEnd={(p) => onSortEndAnswers(p, actual_question.Question.id)}
																	useDragHandle={true}
																	intl={props.intl}
																/>
															</ul>
														)}
													</div>
												</div>
											</div>

											<div id="main_preview" class="gui_question_preview">
												<Layout
													promo={promotion}
													is_builder={true}
													page="quiz"
													only_contents={true}
													custom_layout={{ width:'100' }}
												>
													<TriviaQuestion
														question={actual_question}
														answers={actual_question.Answer}
														total_questions={filtered_questions.length}
														is_preview={true}
													/>
												</Layout>
											</div>
										</div>

										<ButtonCollection in_floating={true}>
											{(error_add !== false) ? (
												<ActionButton
													color="red"
													disabled={true}
													tooltip={<Tooltip
														position="top"
														with_icon={false}
														autotranslate={false}
													>{error_add}</Tooltip>}
												>{button_label}</ActionButton>
											) : (
												<SaveButton
													_class={{color:button_color}}
													onClick={button_action}
												>{button_label}</SaveButton>
											)}

											<SecondaryButton
												onClick={closeQuestionEditLayer}
											>Close</SecondaryButton>

											{(actual_question.Answer.length > 1 && !at_least_one_correct_answer) && (
												<AlertMessage
													title="Click the X to mark the correct answer."
													_class={{ other:'in_top in_bottom' }}
												/>
											)}
										</ButtonCollection>
									</div>
								</FloatingLayer>
							)}
						</BuilderMainWithoutSidebar>
					)
				}} />

				<Route exact={true} path={`/${section_main_path}/design`} render={(route) => {
					const model_color = layout_props_pm.stage.color;
					const model_button = layout_props_pm.stage.button;
					const model_text = layout_props_pm.stage.text;

					const default_field_props = { size:50 };
					const default_layout_props = props.stage?.layout_props ?? {};

					const font_families = [
						{ k:'main', v:i18n("Main") },
						{ k:'secondary', v:i18n("***Secondary") },
					];
				
					const font_weights = [
						{ k:'light', v:"***Light" },
						{ k:'roman', v:"Roman" },
						{ k:'bold', v:"Bold" },
					];
				
					return (
						<BuilderMain pm={questions.pm}>
							<BuilderSidebar onSave={props.saveStage}>
								<NavigationTabsBuilder
									active={route.location.pathname}
									sections={sections}
								>{props.stage_indicator}</NavigationTabsBuilder>

								<article>
									<BuilderSubSectionTitle
										title="Questions"
										icon='knk-questions'
									/>

									<Model model={model_text} change={(k, v) => changeLayout('text', k, v)}>
										<Select
											name='font_family'
											values={font_families}
											_class={default_field_props}
										>Typography</Select>

										<Select
											name='font_weight'
											values={font_weights}
											_class={default_field_props}
										>Font weight</Select>

										<ColorPicker
											name="text_color"
											_class={default_field_props}
											_hide_code={true}
											color={model_text.text_color}
											enable_reset={(default_layout_props?.text?.text_color != undefined) ? true : false}
										>Text color</ColorPicker>

										<Range
											min={1}
											max={3}
											name='font_size'
											default={1.6}
											step={0.2}
											_class={default_field_props}
											label_props={{
												_extra_component:i18n("in 'em'"),
											}}
										>Font size</Range>

										<Range
											min={0.6}
											max={2.4}
											name='line_height'
											default={1.4}
											step={0.2}
											_class={default_field_props}
											label_props={{
												_extra_component:i18n("in 'em'"),
											}}
										>Line height</Range>
									</Model>
								</article>

								<article class="gui_not_in_top_huge">
									<BuilderSubSectionTitle
										title="Backgrounds"
										icon='knk-page'
									/>

									<Model model={props.stage} change={props.changeStage}>
										<Checkbox
											name="display_outter_border"
											value={props.stage?.display_outter_border ?? true}
										>Display outer border</Checkbox>
									</Model>

									<Model model={model_color} change={(k, v) => changeLayout('color', k, v)}>
										<ColorPicker
											name="main_color"
											_class={default_field_props}
											_hide_code={true}
											color={model_color.main_color}
											enable_reset={(default_layout_props?.color?.main_color != undefined) ? true : false}
										>Top half</ColorPicker>

										<ColorPicker
											name="main_color_variant"
											_class={default_field_props}
											_hide_code={true}
											color={model_color.main_color_variant}
											enable_reset={(default_layout_props?.color?.main_color_variant != undefined) ? true : false}
										>Bottom half</ColorPicker>

										<ColorPicker
											name="bar_color"
											_class={default_field_props}
											_hide_code={true}
											color={model_color.bar_color}
											enable_reset={(default_layout_props?.color?.bar_color != undefined) ? true : false}
										>Bar color</ColorPicker>
									</Model>
								</article>

								<article class="gui_not_in_top_huge">
									<BuilderSubSectionTitle
										title="Buttons"
										icon='knk-mouseout'
									/>

									<Model model={model_button} change={(k, v) => changeLayout('button', k, v)}>
										<ColorPicker
											name="background_color"
											_class={default_field_props}
											_hide_code={true}
											color={model_button.background_color}
											enable_reset={(default_layout_props?.button?.background_color != undefined) ? true : false}
										>Background color</ColorPicker>

										<ColorPicker
											name="color"
											_class={default_field_props}
											_hide_code={true}
											color={model_button.color}
											enable_reset={(default_layout_props?.button?.color != undefined) ? true : false}
										>Text color</ColorPicker>
									</Model>
								</article>

								<article class="gui_not_in_top_huge">
									<BuilderSubSectionTitle
										title="Buttons hover"
										icon='knk-mouseover'
									/>

									<Model model={model_button} change={(k, v) => changeLayout('button', k, v)}>
										<ColorPicker
											name="background_color_hover"
											_class={default_field_props}
											_hide_code={true}
											color={model_button.background_color_hover}
											enable_reset={(default_layout_props?.button?.background_color_hover != undefined) ? true : false}
										>Background color</ColorPicker>

										<ColorPicker
											name="color_hover"
											_class={default_field_props}
											_hide_code={true}
											color={model_button.color_hover}
											enable_reset={(default_layout_props?.button?.color_hover != undefined) ? true : false}
										>Text color</ColorPicker>
									</Model>
								</article>

								<article class="gui_not_in_top_huge">
									<BuilderSubSectionTitle
										title="Transition screens"
										icon='knk-on'
									/>

									<Model model={props.stage} change={props.changeStage}>
										<ColorPicker
											key="color_question_ok"
											name="color_question_ok"
											color={stage?.color_question_ok ?? 'rgba(120, 195, 0, 1)'}
											_class={default_field_props}
											enable_reset={(props.stage?.color_question_ok != undefined) ? true : false}
										>OK color</ColorPicker>

										<ColorPicker
											key="color_question_ko"
											name="color_question_ko"
											color={stage?.color_question_ko ?? 'rgba(215, 35, 60, 1)'}
											_class={default_field_props}
											enable_reset={(props.stage?.color_question_ko != undefined) ? true : false}
										>KO Color</ColorPicker>
									</Model>
								</article>
							</BuilderSidebar>

							<BuilderCanvas>
								<Layout
									promo={promotion}
									is_builder={true}
									menus={questions.pm.Custom__Menus}
									page="quiz"
									is_participate={true}
								>
									<TriviaClient
										stage={props.stage}
										questions={setTriviaForBuilder(all_questions, true)}
										is_builder={true}
										promotion={promotion}
										force_game={true}
									/>
								</Layout>
							</BuilderCanvas>
						</BuilderMain>
					)
				}} />

				<Route exact={true} path={`/${section_main_path}/(instructions|gamescore|gamesummary|settings|testreact)`} render={(route) => {
					const option = route.match.params[0];
					const filtered_questions = setTriviaForBuilder(all_questions, true);

					return (
						<BuilderMain pm={questions.pm}>
							<IP locale={promo_locales.locale} messages={promo_locales.messages}>
								<TriviaSidebar
									promo={promotion}
									active={route.location.pathname}
									option={option}
									sections={sections}
									stage={props.stage}
									changeStage={props.changeStage}
									changeMulti={props.changeMulti}
									saveStage={props.saveStage}
									user={user}
									questions={all_questions}
									total_questions={all_questions.length}
									section_main_path={section_main_path}
									onChangeFinalCase={onChangeFinalCase}
								/>
							</IP>

							{(option == 'instructions' && (props.stage?.instructions_disabled ?? false)) && (
								<BuilderCanvas
									fit_to_window="content"
									bordered={false}
									not_awesome={true}
								>
									<p>{i18n("Instructions are disabled.")}</p>
								</BuilderCanvas>
							)}

							{(option != 'instructions' || (props.stage?.instructions_disabled ?? false) === false) && (
								<BuilderCanvas is_adaptable={true} fit_to_window="content">
									<Layout
										promo={promotion}
										is_builder={true}
										menus={questions.pm.Custom__Menus}
										page="quiz"
										is_participate={true}
									>
										<div class="flex_cols">
											<div class="flex_col col2">
												<TriviaClient
													key={option + random_instance}
													submitQuiz={(a, b, c) => { if(c != undefined) c(); } }
													onAnswer={onClientAnswer}
													stage={props.stage}
													questions={filtered_questions}
													is_builder={true}
													promotion={promotion}
													force_game={(option == 'settings') ? true : false}
													force_instructions={(option == 'instructions') ? true : false}
													force_gamescore={(option == 'gamescore') ? true : false}
													force_gamesummary={(option == 'gamesummary') ? true : false}
													is_test={(option == 'testreact') ? true : false}
													canPlayAgain={canPlayAgain}
													final_case={final_case}
												/>
											</div>
										</div>
									</Layout>
								</BuilderCanvas>
							)}
						</BuilderMain>
					)
				}} />
			</Switch>
		</>
	);
};

function select(state) {
	return state;
}

export default connect(select)(injectIntl(TriviaBuilder));